import { Flex, Heading, Skeleton, Text } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';
import { Navigate } from 'react-router';

import { getInitialCalcTermsRows } from './calculationTerminologiesUtils';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { PermissionGuard, PermissionsFn } from '~/core/PermissionGuard';
import { CalculationTerminologiesTable } from '~/core/routes/calculation-terminologies/CalculationTerminologiesTable';
import { colors } from '~/packages/common-frontend/colors';
import { useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery } from '~/packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

// Fragments
const RouteBase = () => {
  const flags = useFeatureFlags();
  const { user } = useUserAuthOrThrow();
  const { facilities } = useCreditFacilitiesOrThrow();

  const creditAgreementQuery = useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery(
    {
      variables: {
        organizationId: user.organization.id,
        creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
      },
    },
  );

  const orderedCalcTermsTableRows = useMemo(() => {
    const creditAgreementArtifacts = creditAgreementQuery.data?.organization.currentCreditAgreementArtifacts.map(
      ({ creditAgreement, id }) => {
        const parsedArtifact = parseCreditAgreementFullArtifact(
          creditAgreement,
        );
        if (!parsedArtifact) {
          throw Error(
            `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
          );
        }
        return parsedArtifact;
      },
    );

    if (!creditAgreementArtifacts) return undefined;

    const calcTermsTableRows = getInitialCalcTermsRows({
      creditAgreementArtifacts,
      creditFacilities: facilities,
    });

    return _.orderBy(calcTermsTableRows, 'termKey', 'asc');
  }, [
    creditAgreementQuery.data?.organization.currentCreditAgreementArtifacts,
    facilities,
  ]);

  if (creditAgreementQuery.error) throw creditAgreementQuery.error;
  if (!orderedCalcTermsTableRows || creditAgreementQuery.loading) {
    return <Skeleton height="700px" />;
  }

  return (
    <>
      <Heading as="h2" mb="1rem">
        Calculation Terminologies
      </Heading>
      <Text color={colors.black[70]} mb="32px">
        Finley&apos;s definition of the Calculation Terminologies.
      </Text>

      <CalculationTerminologiesTable
        calculationTerminologiesTableRows={orderedCalcTermsTableRows}
      />
    </>
  );
};

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug === 'valleyBank') return 'deny';
  return context.flags.productsCalculationTerminologiesPermissions;
};
export const Route = () => (
  <PermissionGuard
    permissions={permissions}
    fallback={<Navigate to="/" replace />}
  >
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/" />;
      }}
    >
      <Flex
        direction="column"
        padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      >
        <RouteBase />
      </Flex>
    </RouteErrorBoundary>
  </PermissionGuard>
);
